<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form
          v-slot="{ values }"
          :validation-schema="schema"
          :initial-values="initial_values"
          class="mt-2 space-y-6"
          @submit="handleCreateInteraction">
          <div class="w-full">
            <SelectTemplateDropDownMenu
              v-if="templates.length"
              v-model="selected_template"
              :items="templates"
              label-attr="name"
              class="relative"
              :is-box-background="true"
              input-classes="sm:text-sm appearance-none h-10 font-medium text-gray-700 text-left block w-full px-3 py-2 w-48 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
              @isEditTemplate="
                ($event) => {
                  is_edit_template = $event
                }
              " />
          </div>
          <div class="flex items-center justify-between gap-1 mb-5 rounded-lg">
            <SelectInput
              label-attr="label"
              value-attr="value"
              :class="$store.state.data.interaction_types.filter((x) => x.value == values['type'])[0]?.categories?.length && 'w-[100%]'"
              class="w-[50%]"
              name="type"
              :input-classes="'text-left'"
              :is-box-background="true"
              :show-label="true"
              :allow-null="false"
              :items="$store.state.data.interaction_types"
              :label="$t('Interaction type')"
              :value="selected_template?.content.type" />

            <SelectInput
              v-if="$store.state.data.interaction_types.filter((x) => x.value == values['type'])[0]?.categories?.length"
              :value="selected_template?.content.category ? selected_template.content.category : values['category']"
              :is-box-background="true"
              :error="category_error_message"
              class="w-[50%]"
              label-attr="label"
              value-attr="value"
              :label="$t('Category')"
              :input-classes="'text-left'"
              :show-label="true"
              name="category"
              :allow-null="false"
              :items="$store.state.data.interaction_types.filter((x) => x.value == values['type'])[0].categories" />

            <div class="flex w-[50%] items-center relative align-center">
              <div class="relative w-full">
                <label
                  for="d_date"
                  class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                  :class="[
                    inputIsFocused || !!interaction_date ? 'top-0' : 'top-1/2',
                    isBoxBackgroud
                      ? 'bg-light-box-background dark:bg-dark-box-background'
                      : 'bg-white text-gray-400 dark:bg-dark-box-background',
                    inputIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                  ]">{{ $t("Due date") }}</label>
                <div class="rounded-md shadow-sm">
                  <DatePicker
                    ref="interaction_date"
                    v-model="interaction_date"
                    :is-dark="$store.state.ui.darkMode"
                    class="bg-white dark:bg-dark-screen-background"
                    is-inline
                    mode="date"
                    :popover="{ visibility: 'click' }">
                    <template #default="{ inputValue, inputEvents }">
                      <div class="relative">
                        <svg
                          fill="none"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>

                        <input
                          id="d_date"
                          :value="inputValue"
                          autocomplete="off"
                          class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                          :class="[
                            !isBoxBackgroud
                              ? 'bg-light-box-background dark:bg-dark-box-background'
                              : 'bg-light-screen-background dark:bg-dark-box-background'
                          ]"
                          @focusin="focusInput"
                          @focusout="focusInput"
                          v-on="inputEvents"
                          @input="
                            $utils.debounce(() => {
                              interaction_date = $event.target.value
                            }, 800)
                          ">
                      </div>
                    </template>

                    <template #footer>
                      <div class="flex items-center justify-end w-full pb-2 pr-2 mt-0">
                        <button v-if="interaction_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDate">
                          {{ $t("Clear") }}
                        </button>
                        <button type="button" class="inline-block cancel-btn" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                      </div>
                    </template>
                  </DatePicker>
                </div>
              </div>
            </div>
          </div>

          <div v-if="values['type'] == 'meeting'" :class="{ flex: !inPlaybook }">
            <SelectInput
              v-model:current-item="start_time"
              :value="start_time"
              :error="start_time_error_message"
              name="start"
              :items="start_time_options.slice(0, start_time_options.length - 1)"
              :label="$t('Start Time')"
              :label-attr="null"
              :value-attr="null"
              :placeholder="$t('Start Time')"
              class="z-1 min-w-[200px]"
              :input-classes="`z-1 'w-[180px]  sm:text-sm appearance-none font-medium text-gray-700 text-left block border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light `" />

            <div v-if="start_time && !inPlaybook" class="relative mt-2 ml-3 mr-2 text-common-grey">
              -
            </div>

            <SelectInput
              v-if="start_time"
              v-model:current-item="end_time"
              :class="{ 'mt-2': inPlaybook }"
              :value="end_time"
              :error="end_time_error_message"
              name="end"
              :items="end_time_options"
              :label="$t('End Time')"
              :label-attr="null"
              :value-attr="null"
              :placeholder="$t('End Time')"
              class="z-1 min-w-[260px]"
              input-classes="z-1 w-[230px] sm:text-sm appearance-none font-medium text-gray-700 text-left block border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
          </div>

          <div class="w-1/2 -mt-6">
            <div v-if="isAllRecords" class="mt-2 whitespace-nowrap text-common-grey">
              {{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("records in table selected") }}
            </div>
            <CompaniesSelect
              v-else
              :multi-select="inPlaybook ? true : false"
              :short-king="inPlaybook"
              :label="$t('Companies/Opportunities')"
              :companies="selected_companies"
              :autocomplete-options="resultReferencesList"
              @changed="
                ($event) => {
                  handleSetCompaniesList($event)
                }
              " />
            <span v-show="required_company_error_message" class="w-1/2 ml-2 text-sm text-red-400 -pt-5">
              {{ required_company_error_message }}
            </span>
          </div>
          <div class="flex w-full">
            <div class="w-1/2 mt-3 mb-5">
              <TeamMembersSelect
                v-model:selected-team-members="selected_users"
                :autocomplete-options="autocompleteOptionsInPlaybook"
                :label="$t('Team Members')" />
            </div>
            <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
              <ContactsSelect v-model:selected-contacts="selected_contacts" label="Contacts" :companies="selected_companies" />
            </div>
          </div>

          <div class="relative rounded-md shadow-sm border p-2 mt-2">
            <div class="flex items-center justify-between w-full">
              <label class="block text-common-grey">{{ $t("Tags") }}</label>
              <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                <span>{{ selected_tags.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
              </button>
            </div>
            <div v-if="selected_tags.length" class="flex flex-wrap gap-2 mt-4">
              <div
                v-for="tag in selected_tags"
                :key="tag?.id"
                class="flex focus:outline-none rounded-md"
                :style="{ background: tag.color }">
                <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                  <span
                    class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                    :style="{
                      background: tag.color
                    }">
                    <span class="block text-white truncate">{{ tag.name }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="cursor-pointer" @click="assign_companies_to_tasks = !assign_companies_to_tasks">
            <span class="mr-2 mt-2 text-common-grey">{{ $t("Assign selected companies to tasks") }} </span>
            <input
              :disabled="true"
              :checked="assign_companies_to_tasks"
              type="checkbox"
              class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-gray-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          </div>

          <div class="cursor-pointer" @click="assign_collaborators_to_tasks = !assign_collaborators_to_tasks">
            <span class="mt-1 mr-2 text-common-grey">{{ $t("Assign selected team members to tasks") }} </span>
            <input
              :disabled="true"
              :checked="assign_collaborators_to_tasks"
              type="checkbox"
              class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-gray-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          </div>

          <div class="flex items-center mt-4 gap-x-2">
            <label class="flex items-baseline justify-between font-medium text-gray-500 text-md">{{ $t("Attached tasks") }}:</label>

            <div
              v-if="!isEditTemplate && !inPlaybook"
              class="btn ml-auto w-fit primary-btn"
              @click="!isEditTemplate && !inPlaybook ? handleCreateTask() : null">
              {{ $t("Create New Task") }}
            </div>

            <Menu v-else
                  v-slot="{ close }"
                  ref="goalsMenu"
                  as="div"
                  class="relative flex-shrink-0 ml-auto">
              <MenuButton
                class="inline-flex items-center justify-center rounded-full w-fit border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                <div class="btn primary-btn">
                  {{ $t("Add Task to Interaction") }}
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                  <div class="py-1 bg-white p-3 border dark:border-dark-divider-border dark:bg-dark-screen-background rounded-lg w-[340px]">
                    <div>
                      <AsyncAutoCompleteInput
                        :add="false"
                        :add-label="$t('Create Task')"
                        :selected-items="selected_tasks"
                        :clear-on-select="true"
                        :is-box-background="true"
                        classes="w-full"
                        input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                        name="task"
                        value-attr="id"
                        :endpoint="`/api/organizations/${$store.state.organization.id}/tasks/`"
                        type="text"
                        :placeholder="$t('Search for task to add')"
                        :debounce="500"
                        @chosen="
                          ($event) => {
                            handleTaskSelected($event, close)
                          }
                        " />
                    </div>
                    <div
                      v-if="!inPlaybook"
                      class="mt-3 mb-3 btn primary-btn"
                      @click="
                        () => {
                          close()
                          handleCreateTask()
                        }
                      ">
                      + {{ $t("Create New Task") }}
                    </div>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>

          <div
            v-if="!tasksCurrentPageItems.length"
            class="relative w-full p-4 mb-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
            <div class="text-lg">
              {{ $t("No tasks to display") }}
            </div>
            <div class="mt-1 text-sm">
              {{ $t("Add new tasks to interaction") }}
            </div>
          </div>

          <div :class="[`grid ${full_screen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 mt-5`]">
            <div v-for="task in tasksCurrentPageItems.slice(0, Math.ceil(tasksCurrentPageItems.length / 2))" :key="task.id">
              <div
                v-if="expanded_tasks.includes(task.id)"
                class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div class="flex items-center">
                  <ToggleStatusButton :object="task" />
                  <div class="ml-2">
                    {{ task.name }}
                  </div>
                  <div class="absolute flex -right-3 -top-3 gap-x-2">
                    <div
                      class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                      @click="handleExpandTask(task)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                      <span
                        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                        {{ $t("Minimize") }}</span>
                    </div>
                    <div
                      class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                      @click="handleViewTask(task)">
                      <Maximize2
                        class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                        color="#2DA7D7" />
                      <span
                        class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("View Task") }}</span>
                    </div>
                    <div
                      class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                      @click="handleRemoveTask(task)">
                      <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                        {{ $t("Remove") }}</span>
                    </div>
                  </div>
                </div>
                <div class="flex w-full mt-5">
                  <div class="w-1/3">
                    <div class="flex flex-row items-center gap-2">
                      <div class="text-common-grey">
                        {{ $t("Priority") }}:
                      </div>

                      <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                        <div v-if="task.priority" class="flex items-center capitalize gap-x-2">
                          <div>
                            {{ task.priority }}
                          </div>
                          <div>
                            <div v-if="task.priority == 'normal'">
                              <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                              <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                            </div>
                            <div v-else-if="task.priority == 'high'">
                              <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'highest'">
                              <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'low'">
                              <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                            </div>
                            <div v-else-if="task.priority == 'lowest'">
                              <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                            </div>
                          </div>
                        </div>

                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>

                  <div class="w-1/3">
                    <div class="flex flex-row items-center gap-2">
                      <div class="text-common-grey whitespace-nowrap">
                        {{ $t("Due Date") }}:
                      </div>

                      <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                        <span v-if="task.due_date">
                          {{ $moment(task.due_date).format("DD MMM  YYYY") }}
                        </span>

                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>

                  <div :class="[`${task.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                    <div
                      :class="[`${task.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                      class="mb-2 text-common-grey whitespace-nowrap text-start">
                      {{ $t("Team Members") }}
                    </div>
                    <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                      <span v-for="user in task.assigned_to.slice(0, 4)" :key="user" class="inline-block has-tooltip">
                        <img
                          v-if="$utils.getUserById(user, 'avatar')"
                          class="inline-block rounded-full h-9 w-9 object-cover"
                          :src="$utils.getUserById(user, 'avatar')"
                          alt="">
                        <Avatar
                          v-else
                          :first-name="$utils.getUserById(user, 'first_name')"
                          :last-name="$utils.getUserById(user, 'last_name')" />
                        <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                          <div class="">
                            <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                              {{ $utils.getUserById(user, "full_name") }}
                            </p>
                          </div>
                        </span>
                      </span>
                      <span
                        v-if="task.assigned_to.length > 4"
                        class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                        + {{ task.assigned_to.length - 4 }} {{ $t("more") }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div
                  :class="[
                    'w-4 h-4 rounded-full mt-1 mr-4 relative',
                    {
                      'bg-dark-brand-blue': ['in-progress', 'pending'].includes(task.status),
                      'bg-status-warning-yellow': task.status == 'overdue',
                      'bg-status-success-green': task.status == 'completed',
                      'bg-status-error-red': ['cancelled', 'missed'].includes(task.status)
                    }
                  ]" />
                <div>{{ task.name }}</div>

                <div class="absolute flex -right-3 -top-3 gap-x-2">
                  <div
                    class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                    @click="handleExpandTask(task)">
                    <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                    <span
                      class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                      {{ $t("Expand") }}</span>
                  </div>
                  <div
                    class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                    @click="handleViewTask(task)">
                    <Maximize2
                      class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                      color="#2DA7D7" />
                    <span
                      class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                      {{ $t("View Task") }}</span>
                  </div>
                  <div
                    class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                    @click="handleRemoveTask(task)">
                    <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                      class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                      {{ $t("Remove") }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-for="task in tasksCurrentPageItems.slice(Math.round(tasksCurrentPageItems.length / 2), tasksCurrentPageItems.length)"
              :key="task.id">
              <div
                v-if="expanded_tasks.includes(task.id)"
                class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div class="flex items-center">
                  <ToggleStatusButton :object="task" />
                  <div class="ml-2">
                    {{ task.name }}
                  </div>
                  <div class="absolute flex -right-3 -top-3 gap-x-2">
                    <div
                      class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                      @click="handleExpandTask(task)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                      <span
                        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                        {{ $t("Minimize") }}</span>
                    </div>
                    <div
                      class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                      @click="handleViewTask(task)">
                      <Maximize2
                        class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                        color="#2DA7D7" />
                      <span
                        class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("View Task") }}</span>
                    </div>
                    <div
                      class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                      @click="handleRemoveTask(task)">
                      <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                        {{ $t("Remove") }}</span>
                    </div>
                  </div>
                </div>
                <div class="flex w-full mt-5">
                  <div class="w-1/3">
                    <div class="flex flex-row items-center gap-2">
                      <div class="text-common-grey">
                        {{ $t("Priority") }}:
                      </div>

                      <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                        <div v-if="task.priority" class="flex items-center capitalize gap-x-2">
                          <div>
                            {{ task.priority }}
                          </div>
                          <div>
                            <div v-if="task.priority == 'normal'">
                              <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                              <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                            </div>
                            <div v-else-if="task.priority == 'high'">
                              <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'highest'">
                              <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'low'">
                              <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                            </div>
                            <div v-else-if="task.priority == 'lowest'">
                              <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                            </div>
                          </div>
                        </div>

                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>

                  <div class="w-1/3">
                    <div class="flex flex-row items-center gap-2">
                      <div class="text-common-grey whitespace-nowrap">
                        {{ $t("Due Date") }}:
                      </div>

                      <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                        <span v-if="task.due_date">
                          {{ $moment(task.due_date).format("DD MMM YYYY") }}
                        </span>

                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>

                  <div :class="[`${task.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                    <div
                      :class="[`${task.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                      class="mb-2 text-common-grey whitespace-nowrap text-start">
                      {{ $t("Team Members") }}
                    </div>
                    <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                      <span v-for="user in task.assigned_to.slice(0, 4)" :key="user" class="inline-block has-tooltip">
                        <img
                          v-if="$utils.getUserById(user, 'avatar')"
                          class="inline-block rounded-full h-9 w-9 object-cover"
                          :src="$utils.getUserById(user, 'avatar')"
                          alt="">
                        <Avatar
                          v-else
                          :first-name="$utils.getUserById(user, 'first_name')"
                          :last-name="$utils.getUserById(user, 'last_name')" />
                        <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                          <div class="">
                            <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                              {{ $utils.getUserById(user, "full_name") }}
                            </p>
                          </div>
                        </span>
                      </span>
                      <span
                        v-if="task.assigned_to.length > 4"
                        class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                        + {{ task.assigned_to.length - 4 }} {{ $t("more") }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div
                  :class="[
                    'w-4 h-4 rounded-full mt-1 mr-4 relative',
                    {
                      'bg-dark-brand-blue': ['in-progress', 'pending'].includes(task.status),
                      'bg-status-warning-yellow': task.status == 'overdue',
                      'bg-status-success-green': task.status == 'completed',
                      'bg-status-error-red': ['cancelled', 'missed'].includes(task.status)
                    }
                  ]" />
                <div>{{ task.name }}</div>

                <div class="absolute flex -right-3 -top-3 gap-x-2">
                  <div
                    class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                    @click="handleExpandTask(task)">
                    <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                    <span
                      class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                      {{ $t("Expand") }}</span>
                  </div>
                  <div
                    class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                    @click="handleViewTask(task)">
                    <Maximize2
                      class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                      color="#2DA7D7" />
                    <span
                      class="tooltip inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                      {{ $t("View Task") }}</span>
                  </div>
                  <div
                    class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                    @click="handleRemoveTask(task)">
                    <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                      class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                      {{ $t("Remove") }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center w-full">
            <div v-if="selected_tasks.length > 6" class="mr-2 text-sm text-common-grey">
              {{ $t("Show") }}
            </div>
            <SelectInput
              v-if="selected_tasks.length > 6"
              :value="tasksPerPage"
              :is-box-background="true"
              class="w-[115px]"
              :input-classes="'text-left'"
              name="tasks_page_show_options"
              :allow-null="false"
              :items="tasks_page_show_options"
              @update:currentItem="tasksPerPage = $event" />
            <div v-if="selected_tasks.length > 6" class="ml-2 text-sm text-common-grey">
              {{ $t("in page") }}
            </div>
            <Pagination
              v-if="selected_tasks.length > tasksPerPage"
              class="ml-auto"
              :total-pages="tasksTotalPages"
              :current-page="tasksCurrentPage"
              @input="tasksChangePage" />
          </div>

          <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
            <TextEditor
              ref="quill"
              v-model:content="interaction_description"
              name="description"
              :label="$t('Description')"
              content-type="html"
              input-classes="rounded-md"
              :mention-items="resultReferencesListForText"
              :value="selected_template?.content?.description"
              theme="bubble"
              toolbar="essential" />
          </div>

          <AttachmentsField v-model="attachments" class="mt-4 mb-6" />

          <div class="flex flex-row justify-between w-full gap-2">
            <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
            <button v-if="!is_edit_template" :disabled="submitting_is_on" type="submit" class="w-1/2 primary-btn">
              <RotateCw
                :class="{
                  'animate-spin': submitting_is_on,
                  'opacity-100': submitting_is_on
                }"
                class="w-5 h-5 mr-3 -ml-1 opacity-0"
                aria-hidden="true" />
              <span class="mr-5"> {{ inPlaybook ? $t("Save") : $t("Create") }} {{ $t("Interaction") }} </span>
            </button>
            <button v-else type="submit" class="w-1/2 primary-btn">
              <RotateCw class="w-5 h-5 mr-3 -ml-1 opacity-0" aria-hidden="true" />
              <span class="mr-5">{{ $t("Update template") }}</span>
            </button>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Add tags -->
  <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Note Deleted')">
    <template #form="{ close: closeModal }">
      <TagsAutoCompleteInput
        :placeholder="$t('Add new tag')"
        value-attr="full_name"
        :selected-tags="selected_tags"
        @changed="
          ($event) => {
            handleTagsChanged($event)
          }
        "
        @close="closeModal" />
    </template>
  </Modal>
</template>

<script>
import * as Yup from "yup"
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import AsyncAutoCompleteInput from "@/components/shared/AsyncAutoCompleteInput.vue"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import Avatar from "@/components/shared/Avatar.vue"
import Pagination from "@/components/shared/Pagination.vue"
import { DatePicker } from "v-calendar"
import SelectTemplateDropDownMenu from "@/components/shared/SelectTemplateDropDownMenu.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Form } from "vee-validate"
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import { Maximize2, ChevronsRight, ChevronRight, Plus, RotateCw, X } from "lucide-vue-next"
import Modal from "@/components/shared/Modal.vue"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import dataService from "@/services/data.service"
import { event as google_event } from "vue-gtag-next"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    ToggleStatusButton,
    AsyncAutoCompleteInput,
    Menu,
    MenuButton,
    MenuItems,
    Maximize2,
    X,
    ChevronRight,
    ChevronsRight,
    Avatar,
    Pagination,
    Plus,
    Modal,
    TagsAutoCompleteInput,
    TextEditor,
    SelectTemplateDropDownMenu,
    AttachmentsField,
    CompaniesSelect,
    SelectInput,
    Form,
    RotateCw,
    DatePicker,
    ContactsSelect,
    TeamMembersSelect
  },

  props: {
    isEditTemplate: {
      type: Boolean,
      default: false
    },
    selectedTemplate: {
      type: Object,
      default: null
    },
    resultReferencesList: null,
    resultReferencesListForText: null,
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    inPlaybook: {
      type: Boolean,
      default: false
    },
    records: {
      type: Array,
      required: false
    },

    record: null,
    contact: {
      type: Object,
      required: false
    },
    isAllRecords: {
      type: Boolean,
      default: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    objectType: {
      type: String,
      required: false
    },
    // autocompleteOptions: {
    //   type: Array,
    //   required: false
    // },
    count: null
  },
  emits: ["cancel", "submitted", "error", "done"],
  setup() {
    return {}
  },
  data() {
    return {
      assign_companies_to_tasks: false,
      assign_collaborators_to_tasks: false,
      selected_tasks: [],
      tasksCurrentPage: 1,
      tasksPerPage: 6,
      assign_company_to_tasks_modal: false,
      full_screen: false,
      expanded_tasks: [],
      task_hover_id: null,
      created_tasks: [],
      submitting_is_on: false,
      is_edit_template: false,
      attachments: [],
      isBoxBackgroud: false,
      interaction_description: null,
      category_error_message: null,
      start_time_error_message: null,
      end_time_error_message: null,
      required_company_error_message: null,
      selected_companies: [],
      follow_up_date: new Date(),
      selected_users: [],
      selected_users_followup: [],
      selected_template: null,
      templates: [],
      selected_contacts: [],
      selected_contacts_followup: [],
      selected_tags: [],
      all_tags: [],
      is_tags_modal_open: false,
      interaction_date: new Date(),
      start_time: null,
      end_time: null,
      start_time_options: [
        "12:00 AM",
        "12:15 AM",
        "12:30 AM",
        "12:45 AM",
        "1:00 AM",
        "1:15 AM",
        "1:30 AM",
        "1:45 AM",
        "2:00 AM",
        "2:15 AM",
        "2:30 AM",
        "2:45 AM",
        "3:00 AM",
        "3:15 AM",
        "3:30 AM",
        "3:45 AM",
        "4:00 AM",
        "4:15 AM",
        "4:30 AM",
        "4:45 AM",
        "5:00 AM",
        "5:15 AM",
        "5:30 AM",
        "5:45 AM",
        "6:00 AM",
        "6:15 AM",
        "6:30 AM",
        "6:45 AM",
        "7:00 AM",
        "7:15 AM",
        "7:30 AM",
        "7:45 AM",
        "8:00 AM",
        "8:15 AM",
        "8:30 AM",
        "8:45 AM",
        "9:00 AM",
        "9:15 AM",
        "9:30 AM",
        "9:45 AM",
        "10:00 AM",
        "10:15 AM",
        "10:30 AM",
        "10:45 AM",
        "11:00 AM",
        "11:15 AM",
        "11:30 AM",
        "11:45 AM",
        "12:00 PM",
        "12:15 PM",
        "12:30 PM",
        "12:45 PM",
        "1:00 PM",
        "1:15 PM",
        "1:30 PM",
        "1:45 PM",
        "2:00 PM",
        "2:15 PM",
        "2:30 PM",
        "2:45 PM",
        "3:00 PM",
        "3:15 PM",
        "3:30 PM",
        "3:45 PM",
        "4:00 PM",
        "4:15 PM",
        "4:30 PM",
        "4:45 PM",
        "5:00 PM",
        "5:15 PM",
        "5:30 PM",
        "5:45 PM",
        "6:00 PM",
        "6:15 PM",
        "6:30 PM",
        "6:45 PM",
        "7:00 PM",
        "7:15 PM",
        "7:30 PM",
        "7:45 PM",
        "8:00 PM",
        "8:15 PM",
        "8:30 PM",
        "8:45 PM",
        "9:00 PM",
        "9:15 PM",
        "9:30 PM",
        "9:45 PM",
        "10:00 PM",
        "10:15 PM",
        "10:30 PM",
        "10:45 PM",
        "11:00 PM",
        "11:15 PM",
        "11:30 PM",
        "11:45 PM",
        "12:00 AM"
      ],
      duration_array: [
        "15 mins",
        "30 mins",
        "45 mins",
        "1 hr",
        "1hr 15min",
        "1.5 hrs",
        "1hr 45min",
        "2 hrs",
        "2hr 15min",
        "2.5 hrs",
        "2hr 45min",
        "3 hrs",
        "3hr 15min",
        "3.5 hrs",
        "3hr 45min",
        "4 hrs",
        "4hr 15min",
        "4.5 hrs",
        "4hr 45min",
        "5 hrs",
        "5hr 15min",
        "5.5 hrs",
        "5hr 45min",
        "6 hrs",
        "6hr 15min",
        "6.5 hrs",
        "6hr 45min",
        "7 hrs",
        "7hr 15min",
        "7.5 hrs",
        "7hr 45min",
        "8 hrs",
        "8hr 15min",
        "8.5 hrs",
        "8hr 45min",
        "9 hrs",
        "9hr 15min",
        "9.5 hrs",
        "9hr 45min",
        "10 hrs",
        "10hr 15min",
        "10.5 hrs",
        "10hr 45min",
        "11 hrs",
        "11hr 15min",
        "11.5  hrs",
        "11hr 45min",
        "12 hrs",
        "12hr 15min",
        "12.5 hrs",
        "12hr 45min",
        "13 hrs",
        "13hr 15min",
        "13.5 hrs",
        "13hr 45min",
        "14 hrs",
        "14hr 15min",
        "14.5 hrs",
        "14hr 45min",
        "15 hrs",
        "15hr 15min",
        "15.5 hrs",
        "15hr 45min",
        "16 hrs",
        "16hr 15min",
        "16.5 hrs",
        "16hr 45min",
        "17 hrs",
        "17hr 15min",
        "17.5 hrs",
        "17hr 45min",
        "18 hrs",
        "18hr 15min",
        "18.5 hrs",
        "18hr 45min",
        "19 hrs",
        "19hr 15min",
        "19.5 hrs",
        "19hr 45min",
        "20 hrs",
        "20hr 15min",
        "20.5 hrs",
        "20hr 45min",
        "21 hrs",
        "21hr 15min",
        "21.5 hrs",
        "21hr 45min",
        "22 hrs",
        "22hr 15min",
        "22.5 hrs",
        "22hr 45min",
        "23 hrs",
        "23hr 15min",
        "23.5 hrs",
        "23hr 45min",
        "24 hrs"
      ],
      initial_values: [],
      end_time_options: [],
      schema: null,
      inputIsFocused: false,
      followupInputIsFocused: false,
      follow_up_date_changed: false
    }
  },
  computed: {
    tasks_page_show_options() {
      const options = [6]
      if (this.selected_tasks.length > 6) {
        options.push(8)
      }
      if (this.selected_tasks.length > 8) {
        options.push(10)
      }
      if (this.selected_tasks.length > 10) {
        options.push(12)
      }
      return options
    },
    tasksTotalPages() {
      return Math.ceil(this.selected_tasks.length / this.tasksPerPage)
    },
    tasksCurrentPageItems() {
      const start = (this.tasksCurrentPage - 1) * this.tasksPerPage
      return this.selected_tasks.slice(start, start + this.tasksPerPage)
    }
  },
  watch: {
    selected_users() {
      this.selected_users_followup = [...this.selected_users]
    },
    selected_contacts() {
      this.selected_contacts_followup = this.selected_contacts
    },
    interaction_date() {
      if (!this.follow_up_date_changed) {
        this.follow_up_date = this.interaction_date
      }
    },
    follow_up_date() {
      this.follow_up_date_changed = true
    },
    start_time() {
      let index = this.start_time_options.findIndex((x) => x == this.start_time)
      const end_time_array = this.start_time_options.slice(index + 1, this.start_time_options.length)

      const end_time_with_duration = []
      end_time_array.forEach((time, i) => {
        end_time_with_duration.push(`${time} (${this.duration_array[i]})`)
      })
      this.end_time_options = end_time_with_duration
      this.end_time = null
      if (this.selected_template?.content?.meta_data?.when?.end_time) {
        this.end_time = this.$moment.unix(this.selected_template?.content?.meta_data.when?.end_time).format("hh:mm A")
      }
    },
    selected_companies() {
      if (this.selected_companies.length > 0) {
        this.required_company_error_message = null
      }
    },
    selected_template() {
      this.interaction_date = this.selected_template?.content?.interaction_date
      if (this.selected_template?.content?.attachments?.length) {
        this.attachments = this.selected_template?.content?.attachments
      }
      this.start_time = this.$moment.unix(this.selected_template?.content?.meta_data?.when?.start_time).format("hh:mm A")
      this.end_time = this.$moment.unix(this.selected_template?.content?.meta_data?.when?.end_time).format("hh:mm A")
      if (this.selected_template?.content?.tags && this.selected_template?.content?.tags.length) {
        this.selected_tags = this.$store.state.data.actions_tags.filter((tag) => this.selected_template.content.tags.includes(tag.id))
      }
      this.interaction_description = this.selected_template?.content?.description
      if (this.selected_template?.content?.tasks && this.selected_template.content.tasks.length) {
        this.selected_template.content.tasks.map((task) => {
          dataService.getTask(task).then((res) => {
            this.selected_tasks.push(res.data)
          })
        })
      }

      if (this.selected_template?.content?.users?.length) {
        this.selected_users = this.selected_template?.content?.users
      }

      if (this.selected_template?.content?.record) {
        if (this.selected_template?.content?.record.id) {
          this.selected_companies.push(this.selected_template?.content?.record)
        } else {
          this.$store.dispatch("data/getRecord", this.selected_template?.content?.record).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
      if (this.selected_template?.content?.contacts?.length) {
        this.selected_template?.content?.contacts.forEach((c) => {
          if (c.id) {
            this.selected_contacts.push(c)
          } else {
            this.$store.dispatch("data/getRecord", c).then((res) => {
              this.selected_contacts.push(res.data)
            })
          }
        })
      }
    }
  },

  activated() {
    this.register_listeners()
  },

  created() {
    if (this.selectedTemplate && this.isEditTemplate) {
      this.is_edit_template = true
      this.selected_template = this.selectedTemplate
    } else if (this.selectedTemplate) {
      this.is_edit_template = false
      this.selected_template = this.selectedTemplate
    }
    const params = { source_object_type: "interaction" }
    dataService.getTemplates(params).then((res) => {
      this.templates = res.data.results
    })
    this.register_listeners()
    this.selected_contacts = this.contact ? [this.contact] : []

    this.schema = Yup.object().shape({
      description: Yup.string(),
      type: Yup.string().required(this.$t("Interaction type is required")).nullable(),
      assigned_to: Yup.array().of(Yup.string())
    })
    this.initial_values = {
      assigned_to: [this.$store.state.auth.user.id],
      type: "call",
      category: "support"
    }
    this.selected_users.push(this.$store.state.auth.user)
    this.selected_users_followup.push(this.$store.state.auth.user)

    this.records?.slice(0, 20)?.forEach((rec) => {
      if (rec.id) {
        this.selected_companies.push(rec)
      } else {
        if (this.$store.state.data.records_fetched[rec]) {
          this.selected_companies.push(this.$store.state.data.records_fetched[rec])
        } else {
          this.$store.dispatch("data/getRecord", rec).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
    })

    if (this.record) {
      if (this.record.id) {
        this.selected_companies.push(this.record)
      } else {
        if (this.$store.state.data.records_fetched[this.record]) {
          this.selected_companies.push(this.$store.state.data.records_fetched[this.record])
        } else {
          this.$store.dispatch("data/getRecord", this.record).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
    }

    if (this.$route.name == "WorkspaceItem") {
      this.$store.dispatch("data/getRecord", this.$store.state.ui.currentWorkspace.record).then((res) => {
        this.selected_companies.push(res.data)
      })
    }
  },
  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  methods: {
    clearDate() {
      this.$refs.interaction_date.hidePopover()
    },

    emptyDate() {
      this.interaction_date = null
      this.$refs.interaction_date.hidePopover()
    },
    handleTaskSelected(task, close) {
      this.selected_tasks.unshift(task)
      this.tasksCurrentPage = 1
      if (this.selected_companies.length && !this.inPlaybook) {
        this.assign_company_to_tasks_modal = true
      }
      close()
    },
    handleCreateTask() {
      this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
        inInteraction: true,
        records: this.selected_companies,
        is_all_records: this.isAllRecords,
        excluded_records: this.excludedRecords
      })
    },
    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },
    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },
    handleViewTask(task) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, { task: this.$utils.clone(task) })
    },
    handleRemoveTask(task) {
      let index = this.selected_tasks.findIndex((g) => g.id == task.id)
      this.selected_tasks.splice(index, 1)
      let index_2 = this.expanded_tasks.findIndex((task) => task == task.id)
      this.expanded_tasks.splice(index_2, 1)
      let index_3 = this.created_tasks.findIndex((t) => t.id == task.id)
      if (index_3 != -1) {
        this.$store.dispatch("data/removeTask", { task: this.created_tasks[index_3] })
        this.created_tasks.splice(index_3, 1)
      }
      const total_pages = Math.ceil(this.selected_tasks.length / this.tasksPerPage)

      if (this.tasksCurrentPage > total_pages) {
        this.tasksCurrentPage = total_pages
      }
    },
    tasksChangePage(page) {
      this.tasksCurrentPage = page
    },
    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_INTERACTION_CREATE_FORM, (tasks) => {
        this.selected_tasks = tasks.concat(this.selected_tasks)
        this.tasksCurrentPage = 1
      })

      this.$emitter.on(ALL_EMITTERS.TASK_UPDATED_IN_INTERACTION_CREATE_FORM, (task) => {
        let index = this.selected_tasks.findIndex((g) => g.id == task.id)
        this.selected_tasks.splice(index, 1, task)
      })

      this.$emitter.on(ALL_EMITTERS.ADD_TAG, (tag) => {
        this.selected_tags.push(tag)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_INTERACTION_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.TASK_UPDATED_IN_INTERACTION_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.ADD_TAG)
    },
    handleSetCompaniesList(e) {
      this.selected_companies = e
    },
    focusInput(followup) {
      if (followup) {
        this.followupInputIsFocused = !this.followupInputIsFocused
      } else {
        this.inputIsFocused = !this.inputIsFocused
      }
    },

    handleCreateInteraction(values) {
      let data = { ...values }
      data["tasks"] = this.selected_tasks.map((t) => t.id)
      data["users"] = this.selected_users.map((x) => (x.id ? x.id : x))
      data["contacts"] = this.selected_contacts.map((x) => (x.id ? x.id : x))

      data["tags"] = this.selected_tags.map((t) => t.id)
      data["description"] = this.interaction_description

      if (this.interaction_date) {
        data["interaction_date"] = this.$moment(this.interaction_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["interaction_date"] = null
      }

      data["is_all_records"] = this.isAllRecords

      data["excluded_records"] = this.excludedRecords
      data["object_type"] = this.objectType

      this.selected_tasks.map((t) => {
        if (this.assign_companies_to_tasks || this.assign_collaborators_to_tasks) {
          let post = {
            task: { id: t.id }
          }
          if (this.assign_companies_to_tasks && this.selected_companies.length) {
            post["task"]["record"] = this.selected_companies[0].id
          }
          if (this.assign_collaborators_to_tasks && this.selected_users.length) {
            post["task"]["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x))
          }
          this.$store.dispatch("data/updateTask", post)
        }
      })
      if (data.type == "meeting") {
        data["meta_data"] = {
          when: {
            object: "timespan",
            end_time: this.$moment(
              `${this.interaction_date.getFullYear()}-${this.interaction_date.getMonth() + 1}-${this.interaction_date.getDate()} ${
                this.end_time?.split(" (")[0]
              }`,
              "YYYY-MM-DD hh:mm A"
            ).unix(),
            start_time: this.$moment(
              `${this.interaction_date.getFullYear()}-${this.interaction_date.getMonth() + 1}-${this.interaction_date.getDate()} ${
                this.start_time
              }`,
              "YYYY-MM-DD hh:mm A"
            ).unix()
          }
        }
      }

      if (this.is_edit_template) {
        //Edit template

        data["tasks"] = this.selected_tasks.map((task) => {
          return { ...task, record: task.record.id ? task.record.id : task.record }
        })
        if (this.selected_companies.length) {
          data["record"] = this.selected_companies[0].id ? this.selected_companies[0].id : this.selected_companies[0]
        }
        let data_template = { ...this.selected_template.content, ...data }
        delete data_template["origin"]
        data_template["organization"] = this.$store.state.organization.id
        data_template["attachments"] = []
        dataService.updateTemplate(this.selected_template.id, { content: data_template }).then((res) => {
          this.$emitter.emit(ALL_EMITTERS.TEMPLATE_UPDATED, res.data)
          this.$emit("done")
        })
      } else {
        //Create new interaction
        if (
          this.$store.state.data.interaction_types.filter((inter) => inter.value === values["type"])[0]?.categories?.length &&
          !values["category"]
        ) {
          this.category_error_message = this.$t("Category is required")
        } else if (!this.start_time && data.type == "meeting") {
          this.start_time_error_message = this.$t("required")
        } else if (!this.end_time && data.type == "meeting") {
          this.end_time_error_message = this.$t("required")
        } else {
          this.submitting_is_on = true

          let post = {
            interaction: { ...data, category: values.category ? values.category : "" },
            records: this.selected_companies.map((c) => c.id),
            is_all_records: this.isAllRecords,
            excluded_records: this.excludedRecords,
            object_type: this.objectType
          }

          if (this.inPlaybook) {
            data["companies"] = this.selected_companies.map((c) => (c.id ? c.id : c))

            if (this.attachments.length) {
              const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
              const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

              let attachment_calc = old_attachments.map((att) => att.id)

              if (new_attachments.length) {
                dataService.addAttachment({ attachments: new_attachments }).then((res) => {
                  attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
                  data["attachments"] = attachment_calc
                  this.$emit("done", data)
                })
              } else {
                data["attachments"] = attachment_calc
                this.$emit("done", data)
              }
            } else {
              this.$emit("done", data)
            }
            google_event("create_interaction_click", {
              event_label: "Create new interaction",
              page: "Playbooks",
              name: "Playbooks",
              user_name: this.$store.state.auth.user.full_name,
              user_email: this.$store.state.auth.user.email,
              organization: this.$store.state.organization.name
            })
          } else {
            this.$store
              .dispatch("data/bulkInteractionCreate", { interaction: post })
              .then((res) => {
                if (this.attachments.length) {
                  let formData = new FormData()
                  this.attachments.forEach((attachment, i) => {
                    Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
                  })

                  res.forEach((interaction) => {
                    this.$store.dispatch("data/addAttachmentsToInteraction", {
                      id: interaction.id,
                      attachments: formData
                    })
                  })
                }

                this.$store.dispatch("ui/hideLoading", null, { root: true })

                this.$emitter.emit(
                  ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED,
                  this.interaction_date ? new Date(this.interaction_date) : null
                )

                this.$emit("done", res[0])
                this.submitting_is_on = false
              })
              .catch(() => {
                this.submitting_is_on = false
                // To do show exceptions
                this.$emit("error")
              })
          }
        }
      }
    }
  }
}
</script>
